<template>
  <div class="rss-page">
    <!-- Header with View Finalized Link -->
    <div class="rss-header d-flex justify-end">
      <v-btn
        color="primary"
        class="rss-header__link"
        @click="navigateToFinalized"
      >
        View Records
      </v-btn>
    </div>

    <!-- Filters -->
    <div>
      <div class="rss-filters__inner">
        <v-autocomplete
          v-model="form.partner"
          :items="partners"
          item-text="name"
          item-value="id"
          return-object
          label="Partner"
          outlined
          dense
          hide-details
          :loading="loadingPartners"
          class="rss-filters__field rss-filters__field--wide"
          @change="onPartnerChange"
        />

        <v-autocomplete
          v-model="form.program_id"
          :items="programs"
          :item-text="programLabel"
          item-value="id"
          label="Programme"
          placeholder="All programmes"
          outlined
          dense
          hide-details
          clearable
          :loading="loadingPrograms"
          :disabled="!form.partner"
          class="rss-filters__field rss-filters__field--wide"
        />

        <v-select
          v-model="form.year"
          :items="yearItems"
          label="Year"
          outlined
          dense
          hide-details
          class="rss-filters__field"
        />

        <v-select
          v-model="form.month"
          :items="monthItems"
          item-text="name"
          item-value="value"
          label="Month"
          outlined
          dense
          hide-details
          class="rss-filters__field"
        />

        <v-select
          v-model="form.fortnight"
          :items="fortnightItems"
          label="Fortnight"
          outlined
          dense
          hide-details
          class="rss-filters__field"
        />

        <v-btn
          depressed
          color="primary"
          height="40"
          :disabled="!form.partner || loadingData"
          :loading="loadingData"
          @click="loadRevenueSplitData"
        >
          Search
        </v-btn>

        <v-btn
          color="primary"
          height="40"
          :disabled="!revenueData.length"
          @click="downloadCsv"
        >
          Download
        </v-btn>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loadingData" class="rss-state">
      <v-progress-circular indeterminate color="primary" size="40" width="3" />
      <p class="rss-state__text">Loading settlement data…</p>
    </div>

    <template v-else-if="revenueData.length">
      <!-- Metrics -->
      <div class="rss-metrics">
        <div v-for="card in summaryCards" :key="card.label" class="rss-metric">
          <div class="rss-metric__icon" :class="`rss-metric__icon--${card.tone}`">
            <v-icon small>{{ card.icon }}</v-icon>
          </div>
          <div class="rss-metric__body">
            <div class="rss-metric__label">{{ card.label }}</div>
            <div class="rss-metric__value">{{ card.value }}</div>
            <div v-if="card.hint" class="rss-metric__hint">{{ card.hint }}</div>
          </div>
        </div>
      </div>

      <div class="rss-section">
        <h5 class="rss-section__title">Settlement Breakdown</h5>
        <span class="rss-section__meta">{{ periodLabel }} · {{ partnerName }}</span>
        <v-spacer />
        <v-btn small text class="rss-section__toggle" @click="toggleAllPrograms">
          <v-icon small left>
            {{ allExpanded ? "mdi-unfold-less-horizontal" : "mdi-unfold-more-horizontal" }}
          </v-icon>
          {{ allExpanded ? "Collapse all" : "Expand all" }}
        </v-btn>
      </div>

      <!-- Programme cards -->
      <div class="rss-programs">
        <v-card
          v-for="(programData, i) in revenueData"
          :key="programData.program_id || i"
          outlined
          class="rss-program"
        >
          <div class="rss-program__header" @click="toggleProgram(i)">
            <div class="rss-program__info">
              <v-icon small color="grey darken-1" class="mr-2">mdi-school-outline</v-icon>
              <span class="rss-program__name">{{ programName(programData) }}</span>
              <v-chip
                v-if="!programData.splits.length"
                x-small
                label
                class="ml-2 rss-program__badge"
              >
                No splits
              </v-chip>
            </div>
            <div class="rss-program__total">
              <span class="rss-program__total-label">Amount</span>
              <span class="rss-program__total-value">
                {{ currency(programData.total_amount) }}
              </span>
              <v-icon
                small
                class="rss-program__chevron"
                :class="{ 'rss-program__chevron--open': isExpanded(i) }"
              >
                mdi-chevron-down
              </v-icon>
            </div>
          </div>

          <v-expand-transition>
            <div v-show="isExpanded(i)">
              <v-divider />
              <div v-if="programData.splits.length" class="rss-table-wrap">
                <table class="rss-table">
                  <thead>
                    <tr>
                      <th class="rss-th">Fee Type</th>
                      <th class="rss-th rss-th--num">Share %</th>
                      <th class="rss-th rss-th--num">Base Amount</th>
                      <th class="rss-th rss-th--num">GST %</th>
                      <th class="rss-th rss-th--num">GST Amount</th>
                      <th class="rss-th">GST Type</th>
                      <th class="rss-th rss-th--num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(split, idx) in programData.splits" :key="idx">
                      <td class="rss-td">
                        <span class="rss-fee">
                          <span class="rss-fee__code">{{ split.fee_code || "—" }}</span>
                          <span class="rss-fee__name">{{ feeLabel(split.fee_code) }}</span>
                        </span>
                      </td>
                      <td class="rss-td rss-td--num">{{ percent(split.fee_percentage) }}</td>
                      <td class="rss-td rss-td--num rss-num">
                        {{ currency(split.amount) }}
                      </td>
                      <td class="rss-td rss-td--num">{{ percent(split.gst_percentage) }}</td>
                      <td class="rss-td rss-td--num rss-num">
                        {{ currency(split.gst_amount) }}
                      </td>
                      <td class="rss-td">
                        <span v-if="num(split.gst_amount)" class="rss-gst-type">
                          {{ split.gst_type || "—" }}
                        </span>
                        <span v-else class="rss-muted">—</span>
                      </td>
                      <td class="rss-td rss-td--num rss-num rss-num--strong">
                        {{ currency(split.total_amount) }}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td class="rss-tf rss-tf--label" colspan="2">Total</td>
                      <td class="rss-tf rss-tf--num rss-num">
                        {{ currency(baseOf(programData)) }}
                      </td>
                      <td class="rss-tf rss-tf--num"></td>
                      <td class="rss-tf rss-tf--num rss-num">
                        {{ currency(gstOf(programData)) }}
                      </td>
                      <td class="rss-tf rss-tf--num"></td>
                      <td class="rss-tf rss-tf--num rss-num rss-num--strong">
                        {{ currency(programData.total_amount) }}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div v-else class="rss-state rss-state--inline">
                <v-icon size="32" color="grey lighten-1">mdi-information-outline</v-icon>
                <p class="rss-state__text">
                  No revenue splits configured for this programme in this period.
                </p>
              </div>
            </div>
          </v-expand-transition>
        </v-card>
      </div>

      <!-- Grand total + finalize, pinned to the bottom -->
      <div class="rss-footbar">
        <div class="rss-grand">
          <div class="rss-grand__text">
            <span class="rss-grand__label">Grand Total</span>
            <span class="rss-grand__period">{{ periodLabel }} · {{ partnerName }}</span>
          </div>
          <span class="rss-grand__value">{{ currency(summaryStats.netPayable) }}</span>
        </div>

        <div class="rss-actions" v-if="num(summaryStats.netPayable) > 0">
          <span class="rss-actions__note">
            Review the split details above before proceeding with finalization
          </span>
          <v-btn
            small
            color="success"
            @click="navigateToFinalize"
            :disabled="!revenueData.length"
          >
            <v-icon small left>mdi-check-circle</v-icon>
            Finalize
          </v-btn>
        </div>
      </div>
    </template>

    <!-- Empty -->
    <v-card v-else-if="searched" outlined class="rss-state rss-state--card">
      <v-icon size="44" color="grey lighten-1">mdi-file-document-outline</v-icon>
      <h3 class="rss-state__title">No settlement data found</h3>
      <p class="rss-state__text">Try a different programme or settlement period.</p>
    </v-card>

    <v-card v-else outlined class="rss-state rss-state--card">
      <v-icon size="44" color="grey lighten-1">mdi-magnify</v-icon>
      <h3 class="rss-state__title">Select filters to begin</h3>
      <p class="rss-state__text">
        Choose a partner and settlement period, then search.
      </p>
    </v-card>
  </div>
</template>

<script>
export default {
  data() {
    const today = new Date();

    return {
      revenueData: [],
      partners: [],
      programs: [],
      feeTypes: [],
      summaryStats: null,
      expandedPrograms: [],

      loadingPartners: false,
      loadingPrograms: false,
      loadingData: false,
      searched: false,

      form: {
        partner: null,
        program_id: null,
        year: today.getFullYear(),
        month: today.getMonth() + 1,
        fortnight: today.getDate() <= 15 ? 1 : 2,
      },

      monthItems: [
        { name: "January", value: 1 },
        { name: "February", value: 2 },
        { name: "March", value: 3 },
        { name: "April", value: 4 },
        { name: "May", value: 5 },
        { name: "June", value: 6 },
        { name: "July", value: 7 },
        { name: "August", value: 8 },
        { name: "September", value: 9 },
        { name: "October", value: 10 },
        { name: "November", value: 11 },
        { name: "December", value: 12 },
      ],

      fortnightItems: [
        { text: "1 - 15", value: 1 },
        { text: "16 - end", value: 2 },
      ],
    };
  },

  computed: {
    yearItems() {
      const y = new Date().getFullYear();
      return [y - 2, y - 1, y, y + 1];
    },

    partnerName() {
      return this.form.partner?.name || "—";
    },

    periodLabel() {
      const month = this.monthItems.find((m) => m.value == this.form.month)?.name;
      const fortnight = this.form.fortnight === 1 ? "1-15" : "16-end";
      return `${fortnight} ${month} ${this.form.year}`;
    },

    summaryCards() {
      if (!this.summaryStats) return [];
      const s = this.summaryStats;
      return [
        {
          label: "Base Amount",
          value: this.currency(s.baseAmount),
          hint: "Partner share before GST",
          icon: "mdi-currency-inr",
          tone: "primary",
        },
        {
          label: "GST",
          value: this.currency(s.gstAmount),
          hint: "Inclusive + exclusive",
          icon: "mdi-percent-outline",
          tone: "amber",
        },
        {
          label: "Amount",
          value: this.currency(s.netPayable),
          hint: "Base + exclusive GST",
          icon: "mdi-cash-check",
          tone: "green",
        },
        {
          label: "Programmes",
          value: `${s.programsWithData} / ${s.programCount}`,
          hint: "With settlement data",
          icon: "mdi-school-outline",
          tone: "grey",
        },
      ];
    },

    allExpanded() {
      return (
        this.revenueData.length > 0 &&
        this.expandedPrograms.length === this.revenueData.length
      );
    },
  },

  created() {
    this.$store.commit("setBreadcrumbs", [
      { text: "Partner Revenue Split", to: { name: "Partner Revenue Split" } },
      { text: "Split Summary" },
    ]);
    this.initialise();
  },

  methods: {
    async initialise() {
      await Promise.all([this.loadPartners(), this.loadFeeTypes()]);

      // The route carries the partner selected on the configuration screen.
      const routeId = Number(this.$route.params.id);
      const partner =
        this.partners.find((p) => p.id === routeId) || this.partners[0] || null;

      if (partner) {
        this.form.partner = partner;
        await this.loadPrograms();
        await this.loadRevenueSplitData();
      }
    },

    async loadPartners() {
      this.loadingPartners = true;
      try {
        const data = await this.apiGet("/partners");
        this.partners = data.filter((p) => p.type !== "Sponsor");
      } catch (err) {
        this.notifyError(err, "Unable to load partners");
      } finally {
        this.loadingPartners = false;
      }
    },

    async loadFeeTypes() {
      try {
        this.feeTypes = await this.apiGet("/fee_types");
      } catch (err) {
        // Fee names are cosmetic — fall back to codes.
        this.feeTypes = [];
      }
    },

    async loadPrograms() {
      this.programs = [];
      if (!this.form.partner) return;

      this.loadingPrograms = true;
      try {
        this.programs = await this.apiGet(
          `/get_partner_programs/${this.form.partner.id}`
        );
      } catch (err) {
        this.notifyError(err, "Unable to load programmes");
      } finally {
        this.loadingPrograms = false;
      }
    },

    async loadRevenueSplitData() {
      if (!this.form.partner) return;

      this.loadingData = true;
      this.searched = true;
      this.expandedPrograms = [];

      const partner = this.form.partner;
      const params = new URLSearchParams({
        partner_id: partner.id,
        partner_code: partner.code,
        year: this.form.year,
        month: this.form.month,
        fortnight: this.form.fortnight,
      });

      if (this.form.program_id) {
        params.append("program_id", this.form.program_id);
      }

      try {
        const data = await this.apiGet(`/revenue_split_histories?${params}`);
        this.revenueData = data.map((item) => ({
          ...item,
          splits: item.splits || [],
        }));
        this.calculateSummaryStats();

        // Expand the first programme that actually has data.
        const firstWithData = this.revenueData.findIndex((p) => p.splits.length);
        if (this.revenueData.length) {
          this.expandedPrograms = [firstWithData > -1 ? firstWithData : 0];
        }
      } catch (err) {
        this.revenueData = [];
        this.summaryStats = null;
        this.notifyError(err, "Unable to load settlement data");
      } finally {
        this.loadingData = false;
      }
    },

    calculateSummaryStats() {
      const baseAmount = this.revenueData.reduce(
        (sum, p) => sum + this.baseOf(p),
        0
      );
      const gstAmount = this.revenueData.reduce(
        (sum, p) => sum + this.gstOf(p),
        0
      );
      const netPayable = this.revenueData.reduce(
        (sum, p) => sum + this.num(p.total_amount),
        0
      );

      this.summaryStats = {
        baseAmount,
        gstAmount,
        netPayable,
        programCount: this.revenueData.length,
        programsWithData: this.revenueData.filter(
          (p) => this.num(p.total_amount) > 0
        ).length,
      };
    },

    baseOf(programData) {
      return programData.splits.reduce((sum, s) => sum + this.num(s.amount), 0);
    },

    gstOf(programData) {
      return programData.splits.reduce((sum, s) => sum + this.num(s.gst_amount), 0);
    },

    isExpanded(index) {
      return this.expandedPrograms.includes(index);
    },

    toggleProgram(index) {
      const at = this.expandedPrograms.indexOf(index);
      if (at > -1) this.expandedPrograms.splice(at, 1);
      else this.expandedPrograms.push(index);
    },

    toggleAllPrograms() {
      this.expandedPrograms = this.allExpanded
        ? []
        : this.revenueData.map((_, index) => index);
    },

    // Backend sends Decimals as strings.
    num(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },

    currency(value) {
      return `₹${new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(this.num(value))}`;
    },

    percent(value) {
      return `${this.num(value)}%`;
    },

    feeLabel(code) {
      return this.feeTypes.find((f) => f.code === code)?.name || code || "";
    },

    programLabel(program) {
      if (!program) return "";
      const name = program.degree?.short_name;
      return `${name}`;
    },

    programName(programData) {
      if (programData.program_name) {
        return programData.program_name.split("-")[0].trim();
      }
      const program = this.programs.find((p) => p.id == programData.program_id);
      return this.programLabel(program) || "";
    },

    onPartnerChange() {
      this.form.program_id = null;
      this.revenueData = [];
      this.summaryStats = null;
      this.expandedPrograms = [];
      this.searched = false;
      this.loadPrograms();
    },

    downloadCsv() {
      const rows = [
        [
          "Programme",
          "Fee Code",
          "Fee Type",
          "Share %",
          "Base Amount",
          "GST %",
          "GST Amount",
          "GST Type",
          "Total",
        ],
      ];

      this.revenueData.forEach((programData) => {
        programData.splits.forEach((s) => {
          rows.push([
            this.programName(programData),
            s.fee_code || "",
            this.feeLabel(s.fee_code),
            this.num(s.fee_percentage),
            this.num(s.amount).toFixed(2),
            this.num(s.gst_percentage),
            this.num(s.gst_amount).toFixed(2),
            s.gst_type || "",
            this.num(s.total_amount).toFixed(2),
          ]);
        });
      });

      rows.push([]);
      rows.push([
        "Grand Total",
        "",
        "",
        "",
        this.summaryStats.baseAmount.toFixed(2),
        "",
        this.summaryStats.gstAmount.toFixed(2),
        "",
        this.summaryStats.netPayable.toFixed(2),
      ]);

      const csv = rows
        .map((row) =>
          row
            .map((cell) => {
              const value = `${cell ?? ""}`;
              return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
            })
            .join(",")
        )
        .join("\n");

      const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `revenue-split-${this.partnerName}-${this.form.year}-${this.form.month}-F${this.form.fortnight}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },

    async apiGet(url) {
      const { data } = await this.$axios.get(url);
      return data.data || [];
    },

    notifyError(err, fallback) {
      this.$store.commit("showSnackbar", {
        message: err?.response?.data?.message || fallback,
        color: "error",
      });
    },

    navigateToFinalize() {
      if (!this.form.partner) {
        this.notifyError(null, "Please select a partner first");
        return;
      }

      this.$router.push({
        name: "RevenueSplitFinalize",
        query: {
          partner_id: this.form.partner.id,
          partner_code: this.form.partner.code,
          partner_name: this.form.partner.name,
          month: this.form.month,
          year: this.form.year,
          fortnight: this.form.fortnight || "",
        },
      });
    },

    navigateToFinalized() {
      this.$router.push({
        name: "RevenueSplitFinalizedList",
        query: this.form.partner ? { partner_id: this.form.partner.id } : {},
      });
    },
  },
};
</script>

<style scoped>
.rss-page {
  --rss-primary: var(--v-primary-base, #00aff1);
  --rss-line: #e4e7ec;
  --rss-ink: #101828;
  --rss-ink-2: #475467;
  --rss-ink-3: #98a2b3;
  padding: 16px 20px 96px;
  min-height: 100%;
}

/* Header */
.rss-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.rss-header__title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--rss-ink);
}

.rss-header__link {
  flex-shrink: 0;
}

.rss-filters__inner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.rss-filters__field {
  flex: 1 1 140px;
  max-width: 180px;
}

.rss-filters__field--wide {
  flex: 1 1 200px;
  max-width: 240px;
}

/* Metrics */
.rss-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.rss-metric {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #fff;
  border: 1px solid var(--rss-line);
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  transition: box-shadow 0.2s, transform 0.2s;
}

.rss-metric:hover {
  box-shadow: 0 4px 12px -2px rgba(16, 24, 40, 0.1);
  transform: translateY(-1px);
}

.rss-metric__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  flex-shrink: 0;
}

.rss-metric__icon--primary {
  background: #e3f2fd;
}
.rss-metric__icon--primary .v-icon {
  color: #1565c0;
}
.rss-metric__icon--amber {
  background: #fff8e1;
}
.rss-metric__icon--amber .v-icon {
  color: #ef6c00;
}
.rss-metric__icon--green {
  background: #e8f5e9;
}
.rss-metric__icon--green .v-icon {
  color: #2e7d32;
}
.rss-metric__icon--grey {
  background: #eceff1;
}
.rss-metric__icon--grey .v-icon {
  color: #455a64;
}

.rss-metric__body {
  min-width: 0;
}

.rss-metric__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--rss-ink-2);
}

.rss-metric__value {
  margin-top: 3px;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--rss-ink);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.2px;
}

.rss-metric__hint {
  margin-top: 3px;
  font-size: 11px;
  color: var(--rss-ink-3);
}

/* Section header */
.rss-section {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin: 24px 0 12px;
}

.rss-section__title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--rss-ink);
}

.rss-section__meta {
  font-size: 12px;
  color: var(--rss-ink-2);
}

.rss-section__toggle {
  align-self: center;
}

/* Programme cards */
.rss-programs {
  display: grid;
  gap: 10px;
}

.rss-program {
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  border-color: var(--rss-line) !important;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.rss-program__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  cursor: pointer;
  transition: background 0.2s;
  user-select: none;
}

.rss-program__header:hover {
  background: #fafbfc;
}

.rss-program__info {
  display: flex;
  align-items: center;
  min-width: 0;
}

.rss-program__name {
  font-size: 14px;
  font-weight: 600;
  color: var(--rss-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rss-program__badge {
  font-size: 10px !important;
  font-weight: 600;
  color: var(--rss-ink-2);
}

.rss-program__total {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.rss-program__total-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--rss-ink-3);
}

.rss-program__total-value {
  font-size: 15px;
  font-weight: 700;
  color: var(--rss-ink);
  font-variant-numeric: tabular-nums;
}

.rss-program__chevron {
  transition: transform 0.25s;
  color: var(--rss-ink-3);
}

.rss-program__chevron--open {
  transform: rotate(180deg);
}

/* Table */
.rss-table-wrap {
  overflow-x: auto;
}

.rss-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 860px;
}

.rss-th {
  padding: 10px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--rss-ink-2);
  background: #f9fafb;
  border-bottom: 1px solid var(--rss-line);
  white-space: nowrap;
}

.rss-th--num {
  text-align: right;
}

.rss-td {
  padding: 10px 16px;
  font-size: 13px;
  color: var(--rss-ink-2);
  border-bottom: 1px solid #f4f5f7;
  white-space: nowrap;
}

.rss-td--num {
  text-align: right;
}

.rss-table tbody tr {
  transition: background 0.15s;
}

.rss-table tbody tr:hover {
  background: #fcfcfd;
}

.rss-tf {
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 700;
  color: var(--rss-ink);
  background: #f9fafb;
  border-top: 1px solid var(--rss-line);
  white-space: nowrap;
}

.rss-tf--label {
  text-align: left;
}

.rss-tf--num {
  text-align: right;
}

.rss-num {
  font-variant-numeric: tabular-nums;
}

.rss-num--strong {
  font-weight: 700;
  color: var(--rss-ink);
}

.rss-fee {
  display: flex;
  align-items: center;
  gap: 10px;
}

.rss-fee__code {
  display: inline-block;
  min-width: 46px;
  padding: 2px 7px;
  border-radius: 5px;
  background: #eef2f6;
  color: #344054;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.rss-fee__name {
  color: var(--rss-ink);
  font-weight: 500;
}

.rss-gst-type {
  font-size: 12px;
  color: var(--rss-ink-2);
}

.rss-muted {
  color: var(--rss-ink-3);
}

/* Bottom bar — grand total and finalize, always fixed to the viewport bottom. */
.rss-footbar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 12px 24px;
  background: #fff;
  border-top: 1px solid var(--rss-line);
  box-shadow: 0 -4px 16px -8px rgba(16, 24, 40, 0.22);
}

/* Permanent navigation drawer starts at the md breakpoint. */
@media (min-width: 960px) {
  .rss-footbar {
    left: 268px;
  }
}

/* Actions */
.rss-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
}

.rss-actions__note {
  font-size: 12px;
  color: var(--rss-ink-3);
}

/* Grand total */
.rss-grand {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-left: 10px;
  border-left: 3px solid var(--rss-primary);
  min-width: 0;
}

.rss-grand__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.rss-grand__label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--rss-ink-2);
}

.rss-grand__period {
  font-size: 12px;
  color: var(--rss-ink-3);
}

.rss-grand__value {
  font-size: 24px;
  font-weight: 700;
  color: var(--rss-ink);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.4px;
}

/* States */
.rss-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 56px 20px;
}

.rss-state--card {
  margin-top: 16px;
  border-radius: 10px;
  background: #fff;
  border-color: var(--rss-line) !important;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.rss-state--inline {
  padding: 32px 20px;
}

.rss-state__title {
  margin: 14px 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--rss-ink);
}

.rss-state__text {
  margin: 0;
  font-size: 13px;
  color: var(--rss-ink-2);
  line-height: 1.55;
}

@media (max-width: 960px) {
  .rss-page {
    padding: 12px 12px 120px;
  }

  .rss-filters__field,
  .rss-filters__field--wide {
    max-width: none;
    flex: 1 1 100%;
  }

  .rss-program__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .rss-program__total {
    width: 100%;
    justify-content: space-between;
  }

  .rss-footbar {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 10px 14px;
  }

  .rss-grand {
    justify-content: space-between;
  }

  .rss-actions {
    justify-content: space-between;
  }

  .rss-actions__note {
    display: none;
  }
}
</style>
